import { AlarmRounded } from "@mui/icons-material";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { ItemProperty } from "src/components";

interface Props {
  dueDate: string | null;
  userCanEdit: boolean;
  disabled?: boolean;
  onSaveClicked: (dueDate: DateTime | null) => void;
  onCancelClicked?: () => void;
}

const DueDateProperty = (props: Props) => {
  const [selectedDueDate, setSelectedDueDate] = useState<DateTime | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setSelectedDueDate(getDateTimeFromISO(props.dueDate));
  }, [props.dueDate]);

  function getDateTimeFromISO(dateTime: string | null) {
    return dateTime != null ? DateTime.fromISO(dateTime) : null;
  }

  function handleSaveClicked() {
    setIsEditing(false);
    props.onSaveClicked(selectedDueDate);
  }

  function handleCancelClicked() {
    setIsEditing(false);
    setSelectedDueDate(getDateTimeFromISO(props.dueDate));
    props.onCancelClicked?.();
  }

  return (
    <ItemProperty
      labelIcon={<AlarmRounded />}
      label="Due"
      labelProps={{ sx: { top: 1 } }}
      canEdit={props.userCanEdit}
      isEditing={isEditing}
      onEditClicked={() => setIsEditing(true)}
    >
      {isEditing ? (
        <Stack spacing={0.5}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DesktopDatePicker
              format="yyyy-MM-dd"
              views={["year", "month", "day"]}
              value={selectedDueDate}
              onChange={(value) => setSelectedDueDate(value)}
              slotProps={{
                field: {
                  clearable: true,
                },
                textField: {
                  fullWidth: true,
                },
                openPickerButton: { size: "small" },
                clearButton: { size: "small", sx: { mr: 0.5 } },
              }}
            />
          </LocalizationProvider>
          <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
            <Button variant="text" disabled={props.disabled} onClick={handleCancelClicked}>
              Cancel
            </Button>
            <Button variant="tertiary" disabled={props.disabled} onClick={handleSaveClicked}>
              Save
            </Button>
          </Stack>
        </Stack>
      ) : selectedDueDate != null ? (
        <Typography sx={{ color: selectedDueDate < DateTime.now() ? theme.palette.error.main : undefined }}>
          {selectedDueDate.toLocaleString(DateTime.DATE_MED)}
        </Typography>
      ) : (
        <Typography variant="placeholder">{props.userCanEdit ? "Set due date..." : "No due date"}</Typography>
      )}
    </ItemProperty>
  );
};

export { DueDateProperty };
